import React, { useEffect, useState, useCallback, useRef } from 'react';
import { getVehicleRouteTimeline } from '../services/routeDetailsAPI';
import { getFullHeadsign } from '../utils/headsigns';
import { formatDelayHuman } from '../utils/time';
import { useResizableSheet } from '../hooks/useResizableSheet';

export default function VehicleBottomSheet({ vehicle, onClose, onStopClick, onTimelineLoaded }) {
    const [timeline, setTimeline] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const scrollRef = useRef(null);
    const { handleRef, sheetStyle, resetHeight } = useResizableSheet(onClose, 0, [31, 62, 85]);

    const fetchTimeline = useCallback(async () => {
        if (!vehicle) return;
        setLoading(true);
        setError(null);
        try {
            const todayStr = new Date().toISOString().split('T')[0];
            const data = await getVehicleRouteTimeline(vehicle, todayStr);
            setTimeline(data);

            // Expose timeline to parent for route stop filtering
            if (onTimelineLoaded) {
                onTimelineLoaded(data);
            }
            
            // Po załadowaniu, po małym re-render, scrollujemy do current stop (lub zaraz przed nim)
            setTimeout(() => {
                if (scrollRef.current) {
                    const currentStop = document.querySelector('.timeline-item.current') || document.querySelector('.timeline-item.future');
                    if (currentStop) {
                        currentStop.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                }
            }, 100);

        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [vehicle]);

    useEffect(() => {
        if (vehicle) {
            fetchTimeline();
            resetHeight();
            const interval = setInterval(fetchTimeline, 30000);
            return () => clearInterval(interval);
        }
    }, [fetchTimeline, vehicle, resetHeight]);

    if (!vehicle) return null;

    const delayInSeconds = vehicle.delay || 0;
    const isLate = delayInSeconds >= 60;
    const isEarly = delayInSeconds <= -60;
    
    // Gdynia (approximated) vs Gdansk
    const isApproximated = vehicle.isApproximated;
    
    // Opcjonalne tło top-bar w zależności od linii lub pojazdu
    const bgClass = vehicle.routeShortName?.startsWith('N') ? 'bg-black text-white' : 'bg-green-700 text-white';

    const renderTimeline = () => {
        if (loading && timeline.length === 0) {
            return <div className="p-8 text-center text-gray-400">Pobieranie trasy przejazdu...</div>;
        }
        
        if (error) {
            return (
                <div className="p-8 text-center text-gray-400">
                    <p>Wystąpił błąd pobierania trasy.</p>
                    <p className="text-sm mt-2">{error}</p>
                </div>
            );
        }

        if (timeline.length === 0) {
            return <div className="p-8 text-center text-gray-400">Brak danych o trasie.</div>;
        }

        return (
            <div className="vehicle-timeline-container" ref={scrollRef}>
                {timeline.map((stop, index) => {
                    const isPast = stop.status === 'past';
                    const isCurrent = stop.status === 'current';
                    
                    const handleStopClick = () => {
                        if (onStopClick && stop.stopLat && stop.stopLon) {
                            onStopClick(stop.stopLat, stop.stopLon);
                        }
                    };

                    return (
                        <div key={stop.id} className={`timeline-item ${stop.status} ${stop.stopLat ? 'clickable' : ''}`} onClick={handleStopClick}>
                            <div className="timeline-times">
                                <span className={`scheduled-time ${isPast ? 'line-through opacity-50' : (isLate || isEarly) ? 'line-through text-gray-400 text-xs' : ''}`}>
                                    {stop.scheduledTime?.substring(0, 5)}
                                </span>
                                {(!isPast && (isLate || isEarly)) && (
                                    <span className={`real-time ${isLate ? 'text-red-500' : 'text-blue-500'}`}>
                                        {stop.realTimeStr}
                                    </span>
                                )}
                            </div>
                            
                            <div className="timeline-node">
                                {isCurrent ? (
                                    <div className="node-current-bus">
                                        <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                                            <path d="M4 16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2H6c-1.1 0-2 .9-2 2v10zM12 5c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6 2.69-6 6-6zm0 2c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zM7.5 14.5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5S6 16.83 6 16s.67-1.5 1.5-1.5zm9 0c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5-1.5-.67-1.5-1.5.67-1.5 1.5-1.5z"></path>
                                        </svg>
                                    </div>
                                ) : (
                                    <div className="node-circle"></div>
                                )}
                                {index < timeline.length - 1 && <div className="node-line"></div>}
                            </div>
                            
                            <div className="timeline-content">
                                <div className="stop-name">{stop.stopName}</div>
                                <div className="stop-status-text">
                                    {isPast ? (
                                        "Odjechał"
                                    ) : (
                                        isLate ? `Opóźniony ${Math.abs(Math.floor(delayInSeconds/60))} min` :
                                        isEarly ? `Przed czasem ${Math.abs(Math.floor(delayInSeconds/60))} min` :
                                        "Punktualnie"
                                    )}
                                </div>
                            </div>
                            
                            <div className="timeline-remaining">
                                {!isPast && (
                                    <>
                                        {stop.remainingMin <= 0 ? (
                                            <span className="text-orange-500 font-bold">&lt;1 min</span>
                                        ) : (
                                            <span><span className="font-bold">{stop.remainingMin}</span> min</span>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const isActive = !!vehicle;

    return (
        <div
            className={`bottom-sheet-container vehicle-sheet-extra ${isActive ? 'active' : ''}`}
            style={sheetStyle}
        >
            <div className="sheet-handle-bar" ref={handleRef} onClick={onClose}>
                <div className="sheet-handle"></div>
            </div>

            <div className="sheet-header" style={{ paddingBottom: 0, paddingTop: '8px', paddingLeft: '12px', paddingRight: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px', width: '100%' }}>
                    <div className={`vehicle-badge flex-shrink-0 ${bgClass}`} style={{ borderRadius: '5px', padding: '2px 8px', fontWeight: 'bold', fontSize: '0.85rem' }}>
                        {vehicle.routeShortName}
                    </div>
                    <div className="vehicle-headsign block truncate flex-grow" style={{ maxWidth: '90%', fontSize: '0.9rem', fontWeight: '600' }}>
                        {getFullHeadsign(vehicle.headsign)}
                    </div>
                    <button className="close-btn" style={{ marginLeft: 'auto', background: 'transparent', border: 'none', fontSize: '1.2rem', cursor: 'pointer' }} onClick={onClose} aria-label="Zamknij panel">
                        &times;
                    </button>
                </div>
            </div>

            <div className="sheet-content" style={{ overflowY: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <div className="vehicle-sheet-scrollable" style={{ flex: 1, overflowY: 'auto', padding: '0 12px 16px' }}>
                    {renderTimeline()}
                </div>
            </div>
        </div>
    );
}
